import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link as MuiLink,
  Grid,
  Tabs,
  Tab,
  useTheme as useMuiTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  NavigateNext as NavigateNextIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Engineering as EngineerIcon,
  Business as BusinessIcon,
  Badge,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTabSearchParam } from '../hooks/useTabSearchParam';
import { hasCapability, CAP } from '../utils/permissions';
import { ROLE } from '../utils/roles';
import { useTheme as useThemeContext } from '../contexts/ThemeContext';
import usersService, { CreateUserData, UpdateUserData, Role, Department, ContractType, Office } from '../services/usersService';
import { User } from '../types';
import LoadingAnimation from '../components/LoadingAnimation';
import PasswordVerificationModal from '../components/PasswordVerificationModal';
import UserAvatar from '../components/UserAvatar';
import RolesSection from '../components/RolesSection';
import AccessDenied from '../components/AccessDenied';

import { authService } from '../services/authService';
import { useUserManagementAccess } from '../hooks/useUserManagementAccess';
import { getZoomCompensatedAutocompleteSlotProps } from '../utils/zoomCompensation';
import { useFormFieldErrors } from '../hooks/useFormFieldErrors';
import FormValidationAlert from '../components/FormValidationAlert';
import { labelsForKeys } from '../utils/formValidation';

type UsersTab = 'personnel' | 'roles';

const USER_FIELD_LABELS: Record<string, string> = {
  name: 'Full name',
  email: 'Email address',
  password: 'Initial password',
  user_office: 'Office',
  department: 'Department',
};

const UsersPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode } = useThemeContext();

  // Design System: Nautilus Console
  const consoleColors = {
    deepOcean: '#0f2134',
    abyssal: '#0a1622',
    sand: '#ccba9b',
    sandDim: 'rgba(204, 186, 155, 0.5)',
    text: '#ffffff',
    textDim: 'rgba(255, 255, 255, 0.7)',
  };
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const canManageRoles = hasCapability(user, CAP.MANAGE_ROLES);
  const allowedTabs = useMemo(() => {
    const tabs: UsersTab[] = ['personnel'];
    if (canManageRoles) tabs.push('roles');
    return tabs;
  }, [canManageRoles]);
  const [activeTab, setActiveTab] = useTabSearchParam(allowedTabs, 'personnel');
  const [createRoleRequested, setCreateRoleRequested] = useState(false);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Password verification states
  const [passwordVerificationOpen, setPasswordVerificationOpen] = useState(false);
  const { isVerified: passwordVerified, grantAccess } = useUserManagementAccess();
  const fieldErrors = useFormFieldErrors();

  // Form states
  const [formData, setFormData] = useState<CreateUserData>({
    name: '',
    trigram: '',
    email: '',
    password: '',
    roles: [],
    user_office: 0,
    department: 0,
    contract_type: undefined,
    intern: false,
    birthday: '',
  });

  // Load users and roles on component mount
  useEffect(() => {
    // Check if password verification is needed
    if (!passwordVerified) {
      setPasswordVerificationOpen(true);
    }
  }, [passwordVerified]);

  useEffect(() => {
    if (passwordVerified) {
      loadUsers();
      loadRoles();
      loadDepartments();
      loadContractTypes();
      loadOffices();
    }
  }, [passwordVerified]);

  const loadUsers = async () => {
    if (!passwordVerified) return;

    try {
      setLoading(true);
      const usersData = await usersService.getUsers();
      setUsers(usersData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const rolesData = await usersService.getRoles();
      setRoles(rolesData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load roles');
    }
  };

  const loadDepartments = async () => {
    try {
      const departmentsData = await usersService.getDepartments();
      setDepartments(departmentsData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load departments');
    }
  };

  const loadContractTypes = async () => {
    try {
      const contractTypesData = await usersService.getContractTypes();
      setContractTypes(contractTypesData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load contract types');
    }
  };

  const loadOffices = async () => {
    try {
      const officesData = await usersService.getOffices();
      setOffices(officesData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load offices');
    }
  };

  const collectUserMissingFields = (isCreate: boolean) => {
    const missing: string[] = [];
    if (!formData.name.trim()) missing.push('name');
    if (!formData.email.trim()) missing.push('email');
    if (isCreate && !formData.password.trim()) missing.push('password');
    if (!formData.user_office) missing.push('user_office');
    if (!formData.department) missing.push('department');
    return missing;
  };

  const handleCreateUser = async () => {
    const missing = collectUserMissingFields(true);
    if (missing.length > 0) {
      fieldErrors.showMissing(missing, labelsForKeys(missing, USER_FIELD_LABELS));
      return;
    }
    try {
      const response = await usersService.createUser(formData);

      if (selectedFile) {
        await usersService.uploadProfilePicture(response.user_id, selectedFile);
      }

      setSuccess('User created successfully');
      setCreateDialogOpen(false);
      resetForm();
      loadUsers();
    } catch (err: any) {
      fieldErrors.show(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    const missing = collectUserMissingFields(false);
    if (missing.length > 0) {
      fieldErrors.showMissing(missing, labelsForKeys(missing, USER_FIELD_LABELS));
      return;
    }

    try {
      const updateData: UpdateUserData = {
        name: formData.name,
        email: formData.email,
        roles: formData.roles,
        user_office: formData.user_office,
        department: formData.department,
        contract_type: formData.contract_type,
        birthday: formData.birthday || undefined,
        trigram: formData.trigram || undefined,
      };

      // Only include password if it was provided
      if (formData.password.trim()) {
        updateData.password = formData.password;
      }

      await usersService.updateUser(selectedUser.id, updateData);

      if (selectedFile) {
        await usersService.uploadProfilePicture(selectedUser.id, selectedFile);
      }

      setSuccess('User updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      resetForm();
      loadUsers();
    } catch (err: any) {
      fieldErrors.show(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      trigram: user.trigram || '',
      email: user.email || '',
      password: '', // Don't pre-fill password
      roles: user.roles.length > 0 ? user.roles : [], // Use existing roles or empty array
      user_office: user.user_office || 0,
      department: user.department || 0,
      contract_type: user.contract_type,
      intern: user.intern || false,
      birthday: user.birthday || '',
    });
    setPreviewUrl(user.profile_picture || null);
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      trigram: '',
      email: '',
      password: '',
      roles: [],
      user_office: 0,
      department: 0,
      contract_type: undefined,
      intern: false,
      birthday: '',
    });
    setSelectedUser(null);
    setSelectedFile(null);
    setPreviewUrl(null);
    fieldErrors.clear();
  };

  const handleCloseDialogs = () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    resetForm();
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case ROLE.ADMIN:
        return 'error';
      case ROLE.LEAD:
        return 'warning';
      case ROLE.PM:
        return 'info';
      case ROLE.ENG:
        return 'success';
      default:
        return 'default';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case ROLE.ADMIN:
        return <AdminIcon sx={{ fontSize: 16 }} />;
      case ROLE.LEAD:
        return <EngineerIcon sx={{ fontSize: 16 }} />;
      case ROLE.PM:
        return <BusinessIcon sx={{ fontSize: 16 }} />;
      case ROLE.ENG:
        return <EngineerIcon sx={{ fontSize: 16 }} />;
      default:
        return <PersonIcon sx={{ fontSize: 16 }} />;
    }
  };

  const handlePasswordVerification = async (password: string) => {
    try {
      const response = await authService.verifyPassword(password);
      return response.data.verified;
    } catch (error) {
      return false;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handlePasswordVerified = () => {
    grantAccess();
    setPasswordVerificationOpen(false);
  };

  // Check if current user is admin
  if (!hasCapability(user, CAP.USER_MANAGEMENT)) {
    return (
      <AccessDenied
        title="Access denied"
        message="You do not have permission to manage users."
      />
    );
  }

  if (!passwordVerified) {
    return (
      <PasswordVerificationModal
        open={passwordVerificationOpen}
        onClose={() => setPasswordVerificationOpen(false)}
        onVerify={handlePasswordVerification}
        onVerified={handlePasswordVerified}
        title="Access User Management"
        description="Please verify your password to access user management features."
      />
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingAnimation />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        mb: 4,
        p: 3,
        backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff',
        border: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.3) : '#e0e0e0'}`,
        borderLeft: `4px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
        boxShadow: mode === 'dark' ? `0 0 20px ${alpha(consoleColors.deepOcean, 0.5)}` : '0 2px 10px rgba(0,0,0,0.05)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Badge sx={{ fontSize: 24, color: mode === 'dark' ? consoleColors.sand : 'primary.main' }} />
            <Typography variant="overline" sx={{
              fontSize: '0.875rem',
              letterSpacing: '0.2em',
              fontWeight: 700,
              color: mode === 'dark' ? consoleColors.sand : 'primary.main'
            }}>
              COMMAND DECK
            </Typography>
          </Box>
          <Typography variant="h4" sx={{
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: mode === 'dark' ? '#fff' : '#1a1a1a',
            fontFamily: 'monospace'
          }}>
            {activeTab === 'roles' ? 'ROLE REGISTRY' : 'FLEET ROSTER'}
          </Typography>
        </Box>
        {!isMobile && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              if (activeTab === 'roles') {
                setCreateRoleRequested(true);
              } else {
                setCreateDialogOpen(true);
              }
            }}
            sx={{
              height: 48,
              px: 3,
              border: `1px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
              color: mode === 'dark' ? consoleColors.sand : theme.palette.primary.main,
              borderRadius: 0,
              fontWeight: 600,
              letterSpacing: '0.1em',
              '&:hover': {
                backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.1) : alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
              }
            }}
          >
            {activeTab === 'roles' ? 'DEFINE ROLE' : 'INITIALIZE PERSONNEL'}
          </Button>
        )}
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, value: UsersTab) => setActiveTab(value)}
        sx={{
          mb: 3,
          borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.2) : '#e0e0e0'}`,
          '& .MuiTab-root': {
            fontFamily: 'monospace',
            letterSpacing: '0.12em',
            fontWeight: 600,
            minHeight: 48,
          },
        }}
      >
        <Tab label="PERSONNEL" value="personnel" />
        {canManageRoles && <Tab label="ROLES" value="roles" />}
      </Tabs>

      {/* Messages */}
      {activeTab === 'personnel' && error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 0, backgroundColor: alpha('#d32f2f', 0.1), color: '#d32f2f', border: '1px solid #d32f2f' }}>
          {error}
        </Alert>
      )}
      {activeTab === 'personnel' && success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 0, backgroundColor: alpha('#388e3c', 0.1), color: '#388e3c', border: '1px solid #388e3c' }}>
          {success}
        </Alert>
      )}

      {activeTab === 'roles' && canManageRoles ? (
        <RolesSection
          createRequested={createRoleRequested}
          onCreateRequestHandled={() => setCreateRoleRequested(false)}
          onRolesChanged={loadRoles}
        />
      ) : (
        <>
      {/* Table */}
      <Paper sx={{
        backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff',
        border: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.3) : '#e0e0e0'}`,
        borderRadius: 0,
        overflow: 'hidden'
      }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: mode === 'dark' ? alpha(consoleColors.deepOcean, 0.5) : '#f5f5f5' }}>
                {['IDENTITY', 'COMMUNICATIONS', 'DESIGNATION', 'ASSIGNMENT', 'COMMANDS'].map((header) => (
                  <TableCell key={header} sx={{
                    color: mode === 'dark' ? alpha(consoleColors.text, 0.7) : 'text.secondary',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    letterSpacing: '0.1em',
                    borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.2) : '#e0e0e0'}`
                  }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} sx={{
                  '&:hover': {
                    backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.05) : alpha(theme.palette.primary.main, 0.02)
                  },
                }}>
                  <TableCell sx={{ borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.1) : '#eee'}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: -2, left: -2, right: -2, bottom: -2,
                          border: `1px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
                          borderRadius: '50%',
                          opacity: 0.5
                        }
                      }}>
                        <UserAvatar user={user} sx={{ width: 32, height: 32 }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{
                          color: mode === 'dark' ? consoleColors.text : 'text.primary',
                          fontWeight: 600,
                          fontFamily: 'monospace'
                        }}>
                          {user.name}
                        </Typography>
                        <Typography variant="caption" sx={{
                          color: mode === 'dark' ? alpha(consoleColors.sand, 0.7) : 'text.secondary',
                          fontFamily: 'monospace',
                          fontSize: '0.7rem'
                        }}>
                          ID: {String(user.id).padStart(6, '0')}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.1) : '#eee'}` }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: mode === 'dark' ? consoleColors.textDim : 'text.secondary' }}>
                      {user.email}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.1) : '#eee'}` }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: mode === 'dark' ? consoleColors.textDim : 'text.secondary' }}>
                      {user.trigram || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.1) : '#eee'}` }}>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {user.roles.map((role) => (
                        <Box key={role} sx={{
                          px: 1, py: 0.25,
                          border: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.5) : '#ccc'}`,
                          fontSize: '0.7rem',
                          fontFamily: 'monospace',
                          color: mode === 'dark' ? consoleColors.sand : 'text.primary',
                          backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.05) : 'transparent'
                        }}>
                          {role.toUpperCase()}
                        </Box>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.1) : '#eee'}` }}>
                    <Tooltip title="MODIFY PROFILE">
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                        sx={{
                          color: mode === 'dark' ? consoleColors.sand : 'primary.main',
                          '&:hover': { backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.1) : alpha(theme.palette.primary.main, 0.1) }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {users.length === 0 && (
          <Box textAlign="center" py={8} sx={{ color: 'text.secondary' }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              NO DATA DETECTED IN ROSTER.
            </Typography>
          </Box>
        )}
      </Paper>
        </>
      )}

      {/* Create User Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCloseDialogs}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff',
            border: `1px solid ${mode === 'dark' ? consoleColors.sand : '#e0e0e0'}`,
            boxShadow: `0 0 24px ${mode === 'dark' ? alpha(consoleColors.deepOcean, 0.8) : 'rgba(0,0,0,0.1)'}`
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.2) : '#e0e0e0'}`,
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: mode === 'dark' ? alpha(consoleColors.deepOcean, 0.5) : '#f8f9fa'
        }}>
          <PersonIcon sx={{ color: mode === 'dark' ? consoleColors.sand : 'primary.main' }} />
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', fontSize: '1rem' }}>
            PERSONNEL INTAKE
          </Typography>
        </DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <Box component="form" noValidate onSubmit={(e) => { e.preventDefault(); handleCreateUser(); }}>
          <DialogContent sx={{ p: 4 }}>
            {/* Same form content but we'll trust standard MUI fields to adapt or could style them if needed. 
                For brevity, using standard fields but wrapped in a consistent container.
            */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="create-user-file"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="create-user-file">
                <Tooltip title="Upload profile picture">
                  <Box sx={{ position: 'relative', cursor: 'pointer' }}>
                    <Box sx={{
                      p: 0.5,
                      border: `1px dashed ${mode === 'dark' ? consoleColors.sand : 'primary.main'}`,
                      borderRadius: '50%'
                    }}>
                      <UserAvatar
                        src={previewUrl || undefined}
                        name={formData.name}
                        sx={{ width: 80, height: 80, fontSize: '2rem' }}
                      />
                    </Box>
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      bgcolor: mode === 'dark' ? consoleColors.sand : 'primary.main',
                      color: mode === 'dark' ? consoleColors.abyssal : 'white',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex'
                    }}>
                      <AddIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Box>
                </Tooltip>
              </label>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  label="FULL NAME"
                  fullWidth
                  required
                  value={formData.name}
                  error={fieldErrors.invalid('name')}
                  onChange={(e) => {
                    fieldErrors.clearField('name');
                    setFormData({ ...formData, name: e.target.value });
                  }}
                  variant="filled"
                  InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="TRIGRAM"
                  fullWidth
                  value={formData.trigram}
                  onChange={(e) => setFormData({ ...formData, trigram: e.target.value })}
                  inputProps={{ maxLength: 10 }}
                  variant="filled"
                  InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="BIRTHDAY"
                  type="date"
                  fullWidth
                  value={formData.birthday}
                  onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                  InputLabelProps={{ shrink: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="EMAIL ADDRESS"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  error={fieldErrors.invalid('email')}
                  onChange={(e) => {
                    fieldErrors.clearField('email');
                    setFormData({ ...formData, email: e.target.value });
                  }}
                  variant="filled"
                  InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="INITIAL PASSWORD"
                  type="password"
                  fullWidth
                  required
                  value={formData.password}
                  error={fieldErrors.invalid('password')}
                  onChange={(e) => {
                    fieldErrors.clearField('password');
                    setFormData({ ...formData, password: e.target.value });
                  }}
                  variant="filled"
                  InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  options={offices}
                  getOptionLabel={(option) => option.country}
                  value={offices.find(o => o.id === formData.user_office) || null}
                  onChange={(_, newValue) => {
                    fieldErrors.clearField('user_office');
                    setFormData({ ...formData, user_office: newValue?.id || 0 });
                  }}
                  slotProps={getZoomCompensatedAutocompleteSlotProps()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="OFFICE"
                      required
                      error={fieldErrors.invalid('user_office')}
                      variant="filled"
                      InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  options={departments}
                  getOptionLabel={(option) => option.department}
                  value={departments.find(d => d.id === formData.department) || null}
                  onChange={(_, newValue) => {
                    fieldErrors.clearField('department');
                    setFormData({ ...formData, department: newValue?.id || 0 });
                  }}
                  slotProps={getZoomCompensatedAutocompleteSlotProps()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="DEPARTMENT"
                      required
                      error={fieldErrors.invalid('department')}
                      variant="filled"
                      InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={roles.map(r => r.name)}
                  value={formData.roles}
                  onChange={(_, newValue) => setFormData({ ...formData, roles: newValue })}
                  slotProps={getZoomCompensatedAutocompleteSlotProps()}
                  renderInput={(params) => <TextField {...params} label="ASSIGNED ROLES" variant="filled" InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }} />}
                />
              </Grid>
            </Grid>

          </DialogContent>
          <DialogActions sx={{ px: 4, pb: 4, gap: 2, borderTop: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.1) : '#eee'}` }}>
            <Button onClick={handleCloseDialogs} sx={{ color: 'text.secondary' }}>CANEL</Button>
            <Button
              type="submit"
              variant="outlined"
              sx={{
                borderRadius: 0,
                border: `1px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
                color: mode === 'dark' ? consoleColors.sand : theme.palette.primary.main,
                fontFamily: 'monospace',
                '&:hover': {
                  backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.1) : alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              CONFIRM INTAKE
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseDialogs}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff',
            border: `1px solid ${mode === 'dark' ? consoleColors.sand : '#e0e0e0'}`,
            boxShadow: `0 0 24px ${mode === 'dark' ? alpha(consoleColors.deepOcean, 0.8) : 'rgba(0,0,0,0.1)'}`
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.2) : '#e0e0e0'}`,
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: mode === 'dark' ? alpha(consoleColors.deepOcean, 0.5) : '#f8f9fa'
        }}>
          <PersonIcon sx={{ color: mode === 'dark' ? consoleColors.sand : 'primary.main' }} />
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', fontSize: '1rem' }}>
            MODIFY RECORD: {selectedUser?.name.toUpperCase()}
          </Typography>
        </DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <Box component="form" noValidate onSubmit={(e) => { e.preventDefault(); handleUpdateUser(); }}>
          <DialogContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="edit-user-file"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="edit-user-file">
                <Tooltip title="Update profile picture">
                  <Box sx={{ position: 'relative', cursor: 'pointer' }}>
                    <Box sx={{
                      p: 0.5,
                      border: `1px dashed ${mode === 'dark' ? consoleColors.sand : 'primary.main'}`,
                      borderRadius: '50%'
                    }}>
                      <UserAvatar
                        user={selectedUser || undefined}
                        src={previewUrl || undefined}
                        name={formData.name}
                        sx={{ width: 80, height: 80, fontSize: '2rem' }}
                      />
                    </Box>
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      bgcolor: mode === 'dark' ? consoleColors.sand : 'primary.main',
                      color: mode === 'dark' ? consoleColors.abyssal : 'white',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex'
                    }}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Box>
                </Tooltip>
              </label>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  label="FULL NAME"
                  fullWidth
                  required
                  value={formData.name}
                  error={fieldErrors.invalid('name')}
                  onChange={(e) => {
                    fieldErrors.clearField('name');
                    setFormData({ ...formData, name: e.target.value });
                  }}
                  variant="filled"
                  InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="TRIGRAM"
                  fullWidth
                  value={formData.trigram}
                  onChange={(e) => setFormData({ ...formData, trigram: e.target.value })}
                  inputProps={{ maxLength: 10 }}
                  variant="filled"
                  InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="BIRTHDAY"
                  type="date"
                  fullWidth
                  value={formData.birthday}
                  onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                  InputLabelProps={{ shrink: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="EMAIL ADDRESS"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  error={fieldErrors.invalid('email')}
                  onChange={(e) => {
                    fieldErrors.clearField('email');
                    setFormData({ ...formData, email: e.target.value });
                  }}
                  variant="filled"
                  InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="UPDATE PASSWORD (OPTIONAL)"
                  type="password"
                  fullWidth
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  variant="filled"
                  InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  options={offices}
                  getOptionLabel={(option) => option.country}
                  value={offices.find(o => o.id === formData.user_office) || null}
                  onChange={(_, newValue) => {
                    fieldErrors.clearField('user_office');
                    setFormData({ ...formData, user_office: newValue?.id || 0 });
                  }}
                  slotProps={getZoomCompensatedAutocompleteSlotProps()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="OFFICE"
                      required
                      error={fieldErrors.invalid('user_office')}
                      variant="filled"
                      InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  options={departments}
                  getOptionLabel={(option) => option.department}
                  value={departments.find(d => d.id === formData.department) || null}
                  onChange={(_, newValue) => {
                    fieldErrors.clearField('department');
                    setFormData({ ...formData, department: newValue?.id || 0 });
                  }}
                  slotProps={getZoomCompensatedAutocompleteSlotProps()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="DEPARTMENT"
                      required
                      error={fieldErrors.invalid('department')}
                      variant="filled"
                      InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={roles.map(r => r.name)}
                  value={formData.roles}
                  onChange={(_, newValue) => setFormData({ ...formData, roles: newValue })}
                  slotProps={getZoomCompensatedAutocompleteSlotProps()}
                  renderInput={(params) => <TextField {...params} label="ASSIGNED ROLES" variant="filled" InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }} />}
                />
              </Grid>
            </Grid>

          </DialogContent>
          <DialogActions sx={{ px: 4, pb: 4, gap: 2, borderTop: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.1) : '#eee'}` }}>
            <Button onClick={handleCloseDialogs} sx={{ color: 'text.secondary' }}>CANCEL</Button>
            <Button
              type="submit"
              variant="outlined"
              sx={{
                borderRadius: 0,
                border: `1px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
                color: mode === 'dark' ? consoleColors.sand : theme.palette.primary.main,
                fontFamily: 'monospace',
                '&:hover': {
                  backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.1) : alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              SAVE RECORDS
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default UsersPage;